import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { debounce } from "~/utils/debounce";
import {
  areModifiersEqual,
  haveSameModifierIds,
  isSameStoredModifierSet,
} from "~/utils/prepareItemsForCart";
import { generateFilterQueryString } from "~/utils/generateFilterQuery";
import { extractErrorMessage, handleInvalidToken } from "~/utils/apiError";
import { rescaleModifiersToLine } from "~/utils/lineModifierQuantities";
import { generateDeliveryWindows } from "~/utils/deliveryWindows";
import type { AppliedModifier, CartMaker } from "~/types";
import type { DeliveryWindow } from "~/utils/deliveryWindows";

// The single name of the persisted-cart key. Exported because three other readers
// (layouts/default.vue and two paths in pages/store/[slug].vue) rehydrate from it; a literal at
// each of those was four places to keep in step.
export const STORED_CART_KEY = "and-dine-cart";

// --------------- Interfaces ---------------

export interface CartProduct {
  id: string | number;
  name: string;
  attachments?: Record<string, unknown>;
  same_day?: boolean;
  future_day?: boolean;
  min_order_amount?: number;
  price_gross?: number;
  price_net?: number;
  price_vat?: number;
  sku?: string;
  category_id?: string | number;
  type?: string;
}

export { AppliedModifier, CartMaker };

export interface CartItem {
  product: CartProduct;
  quantity: number;
  order_product_id?: string | null;
  appliedModifiers: AppliedModifier[];
}

export interface DeliveryDate {
  full: string;
  selectedWindow?: string;
  chosenDate?: { full: string };
  deliveryWindows?: DeliveryWindow[];
  openTime?: string;
  closeTime?: string;
  [key: string]: unknown;
}

export type BillingMethod = "card" | "account";

export interface Cart {
  items: CartItem[];
  maker?: CartMaker;
  date?: DeliveryDate | false;
  deliveryAddress?: Record<string, unknown>;
  billingAddress?: Record<string, unknown>;
  orderNotes?: string;
  deliveryNotes?: string;
  what3words?: string;
  // Drop-point coordinates the customer set at checkout (pin drop / what3words). They
  // ride into the order's delivery_address snapshot so the hub + PedalMe get the exact
  // location, not just the postal address (ops 2026-08-27).
  deliveryLatitude?: number | null;
  deliveryLongitude?: number | null;
  // The address AT the drop-point, reverse-geocoded (mapbox) when the pin was set.
  // Carried on delivery_address so PedalMe's address matches the pin; the typed
  // line_1…zip stay for display/invoicing.
  deliveryGeocodedAddress?: string | null;
  deliveryGeocodedZip?: string | null;
  deliveryPhones?: string[];
  deliveryEmails?: string[];
  deal_id?: string | null;
  cost_centre_id?: string | number | null;
  // Per-order invoice-override fields. Each is a free-text customer input at checkout
  // that the backend resolver uses to override the organisation's invoice default.
  legal_entity?: string | null;
  po_number?: string | null;
  cost_code?: string | null;
  approver_name?: string | null;
  custom_invoice_label?: string | null;
  billing_method?: BillingMethod;
  total?: number;
  items_total?: number;
  [key: string]: unknown;
}

export type InvoiceOverrides = {
  legal_entity?: string | null;
  po_number?: string | null;
  cost_code?: string | null;
  approver_name?: string | null;
  custom_invoice_label?: string | null;
};

/**
 * Cart fields owned by the CUSTOMER, which the calculateCart API response never carries.
 * `setCart` rebuilds `cart.value` from that response, so every field listed here falls
 * back to its previous value when the incoming cart omits it.
 *
 * ANY NEW CUSTOMER-ENTERED CART FIELD MUST BE ADDED HERE. Forgetting is silent and
 * survives testing: the value is correct right up until the first recalculation — a
 * quantity nudge (addToCart / removeFromCart) or the org-resolution recalculateCart on
 * checkout — and then reverts to undefined with no error. That is exactly how
 * `po_number` was lost after being wired into the checkout form: it was set on the cart
 * and read by validateOrder, but blanked in between.
 */
const PRESERVED_CART_FIELDS = [
  "cost_centre_id",
  "billing_method",
  "orderNotes",
  "deliveryNotes",
  "deliveryAddress",
  "billingAddress",
  // Invoice overrides — checkout's "Invoice Details" section writes these via
  // setInvoiceOverrides. Keep in step with the InvoiceOverrides type above.
  "legal_entity",
  "po_number",
  "cost_code",
  "approver_name",
  "custom_invoice_label",
] as const satisfies readonly (keyof Cart)[];

export interface Order {
  id: string | number;
  maker?: CartMaker;
  orderProducts?: unknown[];
  paymentIntent?: unknown;
  rewards?: unknown[];
  status?: string;
  [key: string]: unknown;
}

export const useOrdersStore = defineStore("orders", () => {
  const authStore = useAuthStore();
  const config = useRuntimeConfig();
  const { track } = useTracking();
  const { fetchAttachments } = useAttachments();

  // --------------- Internal Helpers ---------------

  function _authHeaders(): Record<string, string> {
    return {
      accept: "application/json",
      "x-access-token-customer": authStore.token!.token,
      "content-type": "application/json",
    };
  }

  // Shared by all four cart-calculate callers: addToCart, removeFromCart, recreateCart,
  // recalculateCart.
  function _calculateCartFetch<T>(body: Record<string, unknown>): Promise<T> {
    const isCustomer = authStore.isLoggedIn;
    const endpoint = isCustomer
      ? `${config.public.apiBase}/api/customers/orders/calculateCartPublicAndCustomer`
      : `${config.public.apiBase}/api/public/orders/calculateCartPublicAndCustomer`;
    const headers: Record<string, string> = isCustomer ? _authHeaders() : {};
    return $fetch<T>(endpoint, { method: "POST", body, headers }) as Promise<T>;
  }

  function pickAddress(...sources: (Record<string, string> | undefined)[]): {
    zip: string;
    city: string;
    line_1: string;
    line_2: string;
  } {
    const source = sources.find((s) => s?.line_1);
    return {
      zip: source?.zip || "",
      city: source?.city || "",
      line_1: source?.line_1 || "",
      line_2: source?.line_2 || "",
    };
  }

  // --------------- State ---------------
  const cartType = ref<"default" | "premium">("default");
  const cartMode = ref<"create" | "edit">("create");
  const cart = ref<Cart | false>(false);
  const cartError = ref<string | false>(false);
  const checkoutError = ref<string | false>(false);
  // `checkoutError` is shared UI state: the checkout page's own form gates write to it too
  // ("Please fill in your address to continue"), and DynamicForm fires those on mount. The
  // message alone therefore cannot tell a rejected REQUEST from a form the customer simply
  // hasn't finished — which is what the checkout_blocked event needs to know, so the source
  // travels with it. Only meaningful while `checkoutError` is truthy; every truthy write
  // below sets it. See docs/decisions.md 2026-09-10.
  const checkoutErrorSource = ref<"local" | "validate" | "create" | "payment" | "modify">(
    "local",
  );
  function setCheckoutRejection(
    message: string,
    source: "validate" | "create" | "payment" | "modify",
  ) {
    checkoutErrorSource.value = source;
    checkoutError.value = message;
  }
  // Set when a date/time modify was rejected because the courier couldn't be re-booked
  // (backend reverted the date change). Drives a dedicated "courier re-book failed" modal,
  // separate from the generic checkoutError banner.
  const rebookFailedMessage = ref<string | false>(false);
  const guestEmailConflict = ref(false);
  const cartLoading = ref(false);
  const showCart = ref(false);
  const cartLocked = ref(false);
  const order = ref<Order | false>(false);
  const orders = ref<Order[] | false>(false);
  const previousOrders = ref<Order[] | false>(false);
  const ordersLoading = ref(false);
  const chosenOrder = ref<Order | false>(false);
  const orderNotes = ref<string | false>(false);
  const deliveryNotes = ref<string | false>(false);
  const what3words = ref<string | false>(false);
  const deliveryLatitude = ref<number | null>(null);
  const deliveryLongitude = ref<number | null>(null);
  const deliveryGeocodedAddress = ref<string | null>(null);
  const deliveryGeocodedZip = ref<string | null>(null);
  const deliveryPhones = ref<string[]>([""]);
  const deliveryEmails = ref<string[]>([""]);
  const customer = ref<Record<string, string> | false>(false);
  const dealId = ref<string | false>(false);
  const hasUnsavedChanges = ref(false);
  const initialCartItems = ref<CartItem[]>([]);
  const packageCart = ref({
    packages: [] as unknown[],
    items: [] as CartItem[],
    maker: false as CartMaker | false,
    date: false as DeliveryDate | false,
  });

  // Mirror of the in-session Stripe PaymentIntent status. StripePayment.vue
  // writes here when retrievePaymentIntent / confirm{Card,Payment} returns,
  // so the rest of the UI can react to live Stripe state without re-querying
  // the SDK or hammering the active-transaction endpoint. Reset on cart
  // clear / payment success / explicit "start again".
  const stripePaymentIntentStatus = ref<string | null>(null);

  function setStripePaymentIntentStatus(status: string | null) {
    stripePaymentIntentStatus.value = status;
  }

  // Set to true when the customer has actually clicked Pay and Stripe has
  // taken the card details (post elements.submit()). Drives the navigation
  // guard on /checkout — leaving the page mid-payment risks losing the
  // result, so we confirm before allowing it. Also gates the Back button
  // so a misclick doesn't bin the customer's payment.
  //
  // Reset on resume → success, on explicit clearActiveCheckout, or when
  // the wrapper emits "failed" (the payment is no longer in flight, but
  // the sessionStorage pointer is preserved so a refresh resumes to the
  // retry view rather than starting over).
  const paymentInitiated = ref(false);

  function setPaymentInitiated(value: boolean) {
    paymentInitiated.value = value;
  }

  // sessionStorage pointer + Stripe PI status helper for resume flows.
  const { writePointer, readPointer, clearPointer } = useActiveCheckout();
  const { fetchStripePaymentIntentStatus } = useStripe();

  /**
   * Write the active-checkout pointer if the validate/create response
   * carries enough state to drive a resume on tab refresh. No-op when
   * the response is missing a PaymentIntent (cost-centre orders, £0
   * orders) — those flows have nothing to resume.
   */
  function maybeWriteActiveCheckoutPointer(response: Order | undefined | null) {
    if (!response || !response.id) return;
    const pi = response.payment_intent as { id?: string; client_secret?: string } | undefined;
    if (!pi?.id || !pi.client_secret) return;
    writePointer({
      orderId: response.id,
      paymentIntentId: pi.id,
      clientSecret: pi.client_secret,
      initiatedAt: Date.now(),
    });
  }

  /**
   * Resume directive returned to /checkout (and /payment/[id]) on mount.
   * Drives whether to redirect to /success, render the wrapper in a
   * specific initial view, or do nothing because there's nothing to
   * resume.
   */
  type ResumeDirective =
    | { mode: "none" }
    | { mode: "idle"; orderId: string | number }
    | { mode: "confirming"; orderId: string | number }
    | { mode: "failure"; orderId: string | number }
    | { mode: "success"; orderId: string | number };

  /**
   * Read the sessionStorage pointer left behind by validateOrder/createOrder
   * and decide what view the page should mount into. Stripe answers
   * "did the customer click Pay?" — only when it has (processing /
   * succeeded / requires_action) is the backend ledger consulted for the
   * final state. For pre-pay states (requires_payment_method /
   * requires_confirmation) we render the card form, regardless of what
   * the ledger says.
   */
  async function resumeActiveCheckout(): Promise<ResumeDirective> {
    const pointer = readPointer();
    if (!pointer) return { mode: "none" };

    const { orderId, paymentIntentId, clientSecret } = pointer;

    // Rehydrate the order from localStorage so the cart UI has something
    // to render while we probe Stripe + the ledger.
    if (import.meta.client) {
      try {
        const raw = localStorage.getItem(String(orderId));
        if (raw) {
          const stored = JSON.parse(raw) as Order;
          if (stored?.id) setOrder(stored);
        }
      } catch {
        // Corrupt localStorage entry — ignore and continue with whatever
        // state we already had in memory.
      }
    }

    try {
      const piStatus = await fetchStripePaymentIntentStatus(clientSecret);

      if (!piStatus) {
        clearPointer();
        return { mode: "none" };
      }
      if (piStatus === "canceled") {
        clearPointer();
        return { mode: "none" };
      }

      if (piStatus === "requires_payment_method" || piStatus === "requires_confirmation") {
        return { mode: "idle", orderId };
      }

      // Payment has been initiated (requires_action / processing / succeeded).
      // Ask the public ledger-status endpoint for the canonical state.
      let ledgerStatus: string | null = null;
      try {
        const res = await $fetch<{ payment_status: string | null }>(
          `${config.public.apiBase}/api/public/orders/payments/pay-ledger-status/${paymentIntentId}`,
        );
        ledgerStatus = res?.payment_status ?? null;
      } catch {
        // Treat fetch failure as "still settling" rather than guessing.
      }

      if (ledgerStatus === "succeeded") {
        clearPointer();
        paymentInitiated.value = false;
        return { mode: "success", orderId };
      }
      if (ledgerStatus === "failed" || ledgerStatus === "canceled") {
        return { mode: "failure", orderId };
      }
      // pending, requires_action, or null (webhook lag) — keep polling.
      paymentInitiated.value = true;
      return { mode: "confirming", orderId };
    } catch {
      clearPointer();
      return { mode: "none" };
    }
  }

  /**
   * Wipe the active-checkout pointer + reset paymentInitiated. Called
   * after a successful payment redirect, after explicit "Start Again"
   * on the failure view, and from the maker-switch confirm modal.
   */
  function clearActiveCheckout() {
    clearPointer();
    paymentInitiated.value = false;
  }

  // --------------- Getters ---------------
  const cartItemCount = computed(() => {
    if (!cart.value || !cart.value.items) return 0;
    return cart.value.items.reduce((count, item) => count + item.quantity, 0);
  });

  // --------------- Mutations (as actions) ---------------

  function setCartLocked(payload: boolean) {
    cartLocked.value = payload;
  }

  function setPackageCart(payload: Partial<typeof packageCart.value>) {
    packageCart.value = { ...packageCart.value, ...payload };
  }

  function setShowCart(payload: boolean) {
    showCart.value = payload;
  }

  function setCostCentreId(payload: string | number | null) {
    if (cart.value) {
      cart.value = { ...cart.value, cost_centre_id: payload };
    }
  }

  function setInvoiceOverrides(payload: InvoiceOverrides) {
    if (!cart.value) return;
    cart.value = { ...cart.value, ...payload };
  }

  function setBillingMethod(payload: BillingMethod) {
    if (cart.value) {
      cart.value = { ...cart.value, billing_method: payload };
    }
  }

  function setCartType(newType: "default" | "premium") {
    cartType.value = newType;
  }

  function setCartMode(mode: "create" | "edit") {
    cartMode.value = mode;
  }

  function setHasUnsavedChanges(value: boolean) {
    hasUnsavedChanges.value = value;
  }

  function setInitialCartItems(items: CartItem[]) {
    initialCartItems.value = items.map((item) => ({
      product: { ...item.product },
      quantity: item.quantity,
      appliedModifiers: [...item.appliedModifiers],
      order_product_id: item.order_product_id,
    }));
  }

  /**
   * After a successful checkout the current cart *is* the new baseline —
   * not a workaround on top of `leaveStore` (that runs after navigate, too
   * late for `onBeforeRouteLeave`). Snap `initialCartItems` to what was
   * just submitted and clear dirty so the watcher cannot re-arm.
   */
  function resetCart() {
    const items = cart.value && Array.isArray(cart.value.items) ? cart.value.items : [];
    setInitialCartItems(items);
    setHasUnsavedChanges(false);
  }

  function enterStore(options: {
    cartType?: "default" | "premium";
    mode?: "create" | "edit";
    clearLocal?: boolean;
  } = {}) {
    const { cartType: newCartType = "default", mode = "create", clearLocal = false } = options;
    setCartType(newCartType);
    setCartMode(mode);
    clearCartLocal(clearLocal);
    cartLoading.value = false;
    cartError.value = false;
    checkoutError.value = false;
  }

  function leaveStore(options: { clearLocal?: boolean } = {}) {
    const { clearLocal = false } = options;
    setCartType("default");
    setCartMode("create");
    setHasUnsavedChanges(false);
    initialCartItems.value = [];
    clearCartLocal(clearLocal);
    chosenOrder.value = false;
    cartLoading.value = false;
    cartError.value = false;
    checkoutError.value = false;
  }

  function setCart(payload: { cart: Cart | false; isPremium?: boolean }) {
    if (payload.cart) {
      // Preserve user-state fields across cart recalculations (recalculateCart,
      // addItemToCart, removeFromCart) — the calculate API response doesn't
      // carry them, so without this the org-prefilled notes/addresses written
      // by applyCustomerPrefill get blown away by the recalculateCart that
      // fires right after on org load, and the order is created without them.
      const prev = cart.value || ({} as Cart);
      // Fall back to the previous value for each preserved field. An incoming value still
      // wins whenever it is actually present, so a deliberate change is never shadowed —
      // only omissions fall through.
      const keep = <K extends keyof Cart>(key: K): Cart[K] =>
        payload.cart !== false && payload.cart[key] !== undefined ? payload.cart[key] : prev[key];
      cart.value = {
        ...payload.cart,
        ...Object.fromEntries(PRESERVED_CART_FIELDS.map((key) => [key, keep(key)])),
        items: Array.isArray(payload.cart.items) ? payload.cart.items : [],
        date: payload.cart.date || false,
        cost_centre_id:
          payload.cart.cost_centre_id !== undefined
            ? payload.cart.cost_centre_id
            : prev.cost_centre_id,
        billing_method:
          payload.cart.billing_method !== undefined
            ? payload.cart.billing_method
            : prev.billing_method,
        orderNotes:
          payload.cart.orderNotes !== undefined ? payload.cart.orderNotes : prev.orderNotes,
        deliveryNotes:
          payload.cart.deliveryNotes !== undefined
            ? payload.cart.deliveryNotes
            : prev.deliveryNotes,
        what3words:
          payload.cart.what3words !== undefined ? payload.cart.what3words : prev.what3words,
        deliveryLatitude:
          payload.cart.deliveryLatitude !== undefined
            ? payload.cart.deliveryLatitude
            : prev.deliveryLatitude,
        deliveryLongitude:
          payload.cart.deliveryLongitude !== undefined
            ? payload.cart.deliveryLongitude
            : prev.deliveryLongitude,
        deliveryGeocodedAddress:
          payload.cart.deliveryGeocodedAddress !== undefined
            ? payload.cart.deliveryGeocodedAddress
            : prev.deliveryGeocodedAddress,
        deliveryGeocodedZip:
          payload.cart.deliveryGeocodedZip !== undefined
            ? payload.cart.deliveryGeocodedZip
            : prev.deliveryGeocodedZip,
        deliveryPhones:
          payload.cart.deliveryPhones !== undefined
            ? payload.cart.deliveryPhones
            : prev.deliveryPhones,
        deliveryEmails:
          payload.cart.deliveryEmails !== undefined
            ? payload.cart.deliveryEmails
            : prev.deliveryEmails,
        deliveryAddress:
          payload.cart.deliveryAddress !== undefined
            ? payload.cart.deliveryAddress
            : prev.deliveryAddress,
        billingAddress:
          payload.cart.billingAddress !== undefined
            ? payload.cart.billingAddress
            : prev.billingAddress,
      };
      if (payload.cart.items?.length > 0) {
        const date = payload.cart.date;
        cartError.value =
          date && typeof date === "object" && date.selectedWindow
            ? false
            : "Please select a date and time";
      }
      persistCart(payload.isPremium);
    } else {
      cart.value = false;
    }
  }

  // Single writer for the persisted cart — setCart, setDeliveryDate and setSelectedWindow all
  // route through here rather than each calling localStorage.setItem.
  function persistCart(isPremium?: boolean) {
    if (isPremium || !import.meta.client || !cart.value) return;
    localStorage.setItem(STORED_CART_KEY, JSON.stringify(cart.value));
  }

  function clearCartLocal(clearLocal = true) {
    cart.value = false;
    if (clearLocal && import.meta.client) {
      localStorage.removeItem(STORED_CART_KEY);
    }
  }

  function setCartError(payload: string | false) {
    cartError.value = payload;
  }

  function setCartLoading(payload: boolean) {
    cartLoading.value = payload;
  }

  function setOrderNotes(payload: string) {
    orderNotes.value = payload;
    if (cart.value) {
      cart.value = { ...cart.value, orderNotes: payload };
    }
  }

  function setDeliveryNotes(payload: string) {
    deliveryNotes.value = payload;
    if (cart.value) {
      cart.value = { ...cart.value, deliveryNotes: payload };
    }
  }

  function setWhat3Words(payload: string) {
    what3words.value = payload;
    if (cart.value) {
      cart.value = { ...cart.value, what3words: payload };
    }
  }

  // The drop-point coordinates (pin / what3words). Persisted onto the cart so they
  // survive into the createOrder body and land on the order's delivery_address.
  function setDeliveryCoords(lat: number | null, lng: number | null) {
    deliveryLatitude.value = lat;
    deliveryLongitude.value = lng;
    if (cart.value) {
      cart.value = { ...cart.value, deliveryLatitude: lat, deliveryLongitude: lng };
    }
  }

  // The address reverse-geocoded from the drop-point (mapbox), sent to PedalMe so the
  // courier-facing address matches the pin. Persisted on the cart → delivery_address.
  function setDeliveryGeocoded(address: string | null, zip: string | null) {
    deliveryGeocodedAddress.value = address;
    deliveryGeocodedZip.value = zip;
    if (cart.value) {
      cart.value = {
        ...cart.value,
        deliveryGeocodedAddress: address,
        deliveryGeocodedZip: zip,
      };
    }
  }

  function setDeliveryPhones(payload: string[]) {
    deliveryPhones.value = payload;
    if (cart.value) {
      cart.value = { ...cart.value, deliveryPhones: payload };
    }
  }

  function setDeliveryEmails(payload: string[]) {
    deliveryEmails.value = payload;
    if (cart.value) {
      cart.value = { ...cart.value, deliveryEmails: payload };
    }
  }

  function setDeliveryDate(payload: { payload: DeliveryDate; isPremium?: boolean }) {
    if (!cart.value) return;
    const newCart: Cart = {
      ...cart.value,
      items: cart.value.items?.filter((item) => item.product.future_day) || [],
      date: {
        ...cart.value.date,
        ...payload.payload,
      },
    };
    cart.value = newCart;
    if (cart.value.date && !cart.value.date.selectedWindow) {
      cartError.value = "Please select a date and time";
    }
    persistCart(payload.isPremium);
  }

  function setSelectedWindow(payload: { payload: string; isPremium?: boolean }) {
    if (!cart.value) return;
    const date = cart.value.date;
    if (!date || typeof date !== "object") return;
    cart.value = {
      ...cart.value,
      date: { ...date, selectedWindow: payload.payload },
    };
    cartError.value = false;
    // A fresh date/window selection invalidates any stale validation/checkout
    // failure (e.g. "currently selected DD/MM/YYYY is not valid"). Clearing
    // here lets the checkout's `readyToOrder` flip back to true so a
    // re-validate can fire on the next step transition.
    checkoutError.value = false;
    persistCart(payload.isPremium);
  }

  /**
   * Generate delivery windows from the selected date's openTime/closeTime.
   * Mirrors old-site SET_DELIVERY_WINDOWS mutation.
   * Default range: 07:00 – 22:00, offering 30-minute windows with a start
   * every 15 minutes.
   */
  function setDeliveryWindows(datePayload: DeliveryDate | Record<string, unknown>) {
    const open = datePayload?.openTime as string | undefined;
    const close = datePayload?.closeTime as string | undefined;

    const windows = generateDeliveryWindows(open, close);

    if (cart.value && cart.value.date && typeof cart.value.date === "object") {
      cart.value = {
        ...cart.value,
        date: { ...cart.value.date, deliveryWindows: windows },
      };
    }
  }

  function setCustomer(payload: Record<string, string> | false) {
    customer.value = payload;
    if (payload && import.meta.client) {
      localStorage.setItem("customer", JSON.stringify(payload));
    }
  }

  function setDeliveryAddress(payload: Record<string, unknown> | false) {
    if (payload && import.meta.client) {
      localStorage.setItem("address", JSON.stringify(payload));
    }
    if (cart.value) {
      cart.value = { ...cart.value, deliveryAddress: payload || undefined };
    }
  }

  function setBillingAddress(payload: Record<string, unknown> | false) {
    if (cart.value) {
      cart.value = { ...cart.value, billingAddress: payload || undefined };
    }
  }

  function setOrder(payload: Order | false) {
    order.value = payload;
    if (import.meta.client) {
      if (payload) {
        localStorage.setItem(String(payload.id), JSON.stringify(payload));
      } else if (order.value && order.value !== false) {
        localStorage.removeItem(String(order.value.id));
      }
    }
  }

  function setChosenOrder(payload: Order | false) {
    chosenOrder.value = payload;
    if (!payload && chosenOrder.value && import.meta.client) {
      localStorage.removeItem(String((chosenOrder.value as Order).id));
    }
  }

  function setOrders(payload: Order[] | false) {
    orders.value = payload || false;
  }

  function setPreviousOrders(payload: Order[] | false) {
    previousOrders.value = payload || false;
  }

  function setCheckoutError(payload: string | false) {
    // Page-authored copy (form gates, step guards) — never a rejected request.
    checkoutErrorSource.value = "local";
    checkoutError.value = payload;
  }

  function setDealId(payload: string | false) {
    dealId.value = payload;
  }

  // --------------- Actions ---------------

  function clearCart(options: { clearLocal?: boolean } = {}) {
    const { clearLocal = true } = options;
    clearCartLocal(clearLocal);
    order.value = false;
    chosenOrder.value = false;
    cartLoading.value = false;
    cartError.value = false;
    checkoutError.value = false;
  }

  /**
   * A maker the cart points at is definitively gone (deactivated or removed).
   * That cart can never be ordered, so clear it rather than leaving the
   * customer to land on a dead cart on every visit, and tell them why.
   *
   * No-op unless the cart in memory belongs to `slug`. Returns whether it did,
   * so the caller can decide on a redirect. Only ever call this for a
   * definitive signal (a 404 from the public makers endpoint, which filters
   * inactive makers) — never for a transient failure, or a page-load blip
   * would wipe an in-progress order.
   */
  function clearCartForUnavailableMaker(slug: string): boolean {
    if (cart.value?.maker?.slug !== slug) return false;
    clearCart();
    notifyQueued("The maker in your cart is no longer available, so your cart has been cleared.");
    return true;
  }

  function clearOrder(id: string) {
    if (import.meta.client) {
      localStorage.removeItem(id);
    }
  }

  const addToCart = debounce(
    async (payload: {
      product?: CartProduct;
      quantity: number;
      appliedModifiers: AppliedModifier[];
      maker: CartMaker;
      date?: DeliveryDate | false;
      deal_id?: string | null;
      isPremium?: boolean;
      order_product_id?: string | null;
      // "add" sums with the matching existing item (product modal default).
      // "set" replaces — used by the cart row's +/- operator which emits the new total.
      mode?: "add" | "set";
    }) => {
      let updatedItems = cart.value && cart.value.items ? [...cart.value.items] : [];
      const makerId = payload.maker?.id || (cart.value ? cart.value.maker?.id : undefined);
      const mode = payload.mode ?? "set";

      // appliedModifiers arrive ALREADY SCALED to the line — see
      // app/utils/lineModifierQuantities.ts for the contract and why the scaling lives at the
      // call sites rather than here. This must not scale: CartSummary's +/- operator passes an
      // existing cart item's modifiers straight back in, so multiplying here would scale them a
      // second time on every quantity change.
      if (payload.product) {
        // Which comparison applies is decided by the mode, because they are different questions.
        // "set" REPLACES a line the caller has already resolved (the cart operator, which hands back an
        // array it just rescaled — so neither the stored totals nor the per-unit ratio still match).
        // "add" MERGES a fresh pick into a line of the same kind, which is the per-unit question.
        const index = updatedItems.findIndex(
          (item) =>
            item.product.id === payload.product!.id &&
            (mode === "set"
              ? haveSameModifierIds(item.appliedModifiers, payload.appliedModifiers)
              : areModifiersEqual(
                  item.appliedModifiers,
                  item.quantity,
                  payload.appliedModifiers,
                  payload.quantity,
                )),
        );

        if (index === -1) {
          updatedItems.push({
            product: payload.product,
            quantity: payload.quantity,
            order_product_id: null,
            appliedModifiers: payload.appliedModifiers,
          });
        } else {
          const mergedQuantity =
            mode === "set" ? payload.quantity : updatedItems[index]!.quantity + payload.quantity;
          updatedItems = [
            ...updatedItems.slice(0, index),
            {
              product: payload.product,
              quantity: mergedQuantity,
              order_product_id: payload.order_product_id ?? null,
              // No-op under mode "set" (mergedQuantity === payload.quantity), which is what every
              // production caller uses. Under mode "add" the merged line is longer than the one the
              // caller scaled against, so the modifiers have to follow it or the extra units carry
              // no add-on.
              appliedModifiers: rescaleModifiersToLine(
                payload.appliedModifiers,
                payload.quantity,
                mergedQuantity,
              ),
            },
            ...updatedItems.slice(index + 1),
          ].filter((item) => item.quantity > 0);
        }
      }

      const body = {
        items: updatedItems.map((item) => ({
          product_id: item.product.id,
          name: item.product.name,
          quantity: item.quantity,
          appliedModifiers: item.appliedModifiers,
          order_product_id: item.order_product_id || null,
        })),
        maker_id: makerId,
      };

      try {
        const response = await _calculateCartFetch<Record<string, unknown>>(body);

        const newCart: Cart = {
          ...response,
          deal_id: payload.deal_id || null,
          maker: {
            id: payload.maker.id,
            name: payload.maker.name,
            slug: payload.maker.slug,
            address: payload.maker.address,
            delivery_days: payload.maker.delivery_days,
            next_available: payload.maker.next_available,
            free_delivery_min_amount: payload.maker.free_delivery_min_amount,
            min_order_amount: payload.maker.min_order_amount || 1500,
          },
          date: payload.date || false,
          items: updatedItems.map((item) => ({
            product: {
              id: item.product.id,
              name: item.product.name,
              attachments: item.product.attachments,
              same_day: item.product.same_day,
              future_day: item.product.future_day,
              min_order_amount: item.product.min_order_amount,
              price_gross: item.product.price_gross,
              price_net: item.product.price_net,
              price_vat: item.product.price_vat,
            },
            order_product_id: item.order_product_id,
            quantity: item.quantity,
            appliedModifiers: item.appliedModifiers,
          })),
        } as Cart;

        cartLoading.value = false;
        setCart({ cart: newCart, isPremium: payload.isPremium });

        // Mixpanel: product added
        if (payload.product) {
          track("product_added_to_cart", {
            product_id: payload.product.id,
            product_name: payload.product.name,
            maker_slug: payload.maker?.slug || "",
            quantity: payload.quantity,
            cart_total: (newCart.total || 0) / 100,
            cart_item_count: newCart.items?.length || 0,
          });
        }

        // GTM: add_to_cart
        if (typeof window !== "undefined" && (window as any).dataLayer) {
          (window as any).dataLayer.push({
            event: "add_to_cart",
            currency: "GBP",
            value: (newCart.total || 0) / 100,
            items: updatedItems.map((item) => ({
              item_id: item.product.id,
              item_name: item.product.name,
              quantity: item.quantity,
              price: (item.product.price_gross || 0) / 100,
            })),
          });
        }
      } catch (error) {
        cartLoading.value = false;
        // Surface the rejection instead of dropping it. The customer pressed a control, so silence
        // reads as "nothing happened" — and the cart still holds the state the backend just refused,
        // which is the shape of failure this contract makes most likely: a split group whose shares no
        // longer sum to the line quantity comes back as ModSplitQtyMismatch, and the caller cannot tell
        // that from a network blip.
        cartError.value = extractErrorMessage(error);
      }
    },
    200,
  );

  const removeFromCart = debounce(
    async (payload: {
      product: CartProduct;
      quantity: number;
      appliedModifiers: AppliedModifier[];
      maker: CartMaker;
      date?: DeliveryDate | false;
      isPremium?: boolean;
      order_product_id?: string | null;
    }) => {
      if (!cart.value) return;

      let updatedItems = [...cart.value.items];
      const index = updatedItems.findIndex(
        (item) =>
          item.product.id === payload.product.id &&
          // Locating the row from its own array — exact stored totals.
          isSameStoredModifierSet(item.appliedModifiers, payload.appliedModifiers || []),
      );
      if (index === -1) return;

      updatedItems = [
        ...updatedItems.slice(0, index),
        {
          product: payload.product,
          quantity: payload.quantity,
          appliedModifiers: payload.appliedModifiers,
          order_product_id: payload.order_product_id,
        },
        ...updatedItems.slice(index + 1),
      ].filter((item) => item.quantity > 0);

      cartLoading.value = true;
      const body = {
        items: updatedItems.map((item) => ({
          product_id: item.product.id,
          name: item.product.name,
          quantity: item.quantity,
          appliedModifiers: item.appliedModifiers,
          order_product_id: item.order_product_id || null,
        })),
        maker_id: payload.maker.id,
      };

      try {
        const response = await _calculateCartFetch<Record<string, unknown>>(body);

        const newCart: Cart = {
          ...response,
          maker: {
            id: payload.maker.id,
            name: payload.maker.name,
            slug: payload.maker.slug,
            address: payload.maker.address,
            delivery_days: payload.maker.delivery_days,
            next_available: payload.maker.next_available,
            free_delivery_min_amount: payload.maker.free_delivery_min_amount,
            min_order_amount: payload.maker.min_order_amount || 1500,
          },
          date: payload.date || false,
          items: updatedItems.map((item) => ({
            product: {
              id: item.product.id,
              name: item.product.name,
              attachments: item.product.attachments,
              same_day: item.product.same_day,
              future_day: item.product.future_day,
              min_order_amount: item.product.min_order_amount,
              price_gross: item.product.price_gross,
              price_net: item.product.price_net,
              price_vat: item.product.price_vat,
            },
            quantity: item.quantity,
            order_product_id: item.order_product_id,
            appliedModifiers: item.appliedModifiers,
          })),
        } as Cart;

        cartLoading.value = false;
        setCart({ cart: newCart, isPremium: payload.isPremium });

        // Mixpanel: product removed
        track("product_removed_from_cart", {
          product_id: payload.product.id,
          product_name: payload.product.name,
          maker_slug: payload.maker?.slug || "",
        });

        // GTM: remove_from_cart
        if (typeof window !== "undefined" && (window as any).dataLayer) {
          (window as any).dataLayer.push({
            event: "remove_from_cart",
            currency: "GBP",
            value: (newCart.total || 0) / 100,
            items: updatedItems.map((item) => ({
              item_id: item.product.id,
              item_name: item.product.name,
              quantity: item.quantity,
              price: (item.product.price_gross || 0) / 100,
            })),
          });
        }
      } catch {
        cartLoading.value = false;
      }
    },
    100,
  );

  async function recreateCart(payload: {
    items: CartItem[];
    maker: CartMaker;
    date?: DeliveryDate | false;
    deal_id?: string | null;
    isPremium?: boolean;
  }) {
    const body = {
      items: payload.items.map((item) => ({
        product_id: item.product.id,
        name: item.product.name,
        quantity: item.quantity,
        appliedModifiers: item.appliedModifiers,
        order_product_id: item.order_product_id || null,
      })),
      maker_id: payload.maker.id,
    };

    try {
      const response = await _calculateCartFetch<
        { orderProducts: unknown[] } & Record<string, unknown>
      >(body);

      const newCart: Cart = {
        ...response,
        deal_id: payload.deal_id || null,
        maker: {
          id: payload.maker.id,
          name: payload.maker.name,
          slug: payload.maker.slug,
          address: payload.maker.address,
          delivery_days: payload.maker.delivery_days,
          next_available: payload.maker.next_available,
          free_delivery_min_amount: payload.maker.free_delivery_min_amount,
          min_order_amount: payload.maker.min_order_amount || 1500,
        },
        date: payload.date || false,
        items: payload.items.map((item) => ({
          product: item.product,
          order_product_id: item.order_product_id || null,
          quantity: item.quantity,
          appliedModifiers: item.appliedModifiers,
        })),
      } as Cart;

      cartLoading.value = false;
      setCart({ cart: newCart, isPremium: payload.isPremium });
      return newCart.items;
    } catch (error) {
      cartLoading.value = false;
      console.error("[recreateCart] calculate failed", error);
    }
  }

  async function recalculateCart() {
    if (!cart.value || !cart.value.items?.length || !cart.value.maker) return;

    const body = {
      items: cart.value.items.map((item) => ({
        product_id: item.product.id,
        name: item.product.name,
        quantity: item.quantity,
        appliedModifiers: item.appliedModifiers,
        order_product_id: item.order_product_id || null,
      })),
      maker_id: cart.value.maker.id,
    };

    cartLoading.value = true;
    try {
      const response = await _calculateCartFetch<Record<string, unknown>>(body);
      const newCart: Cart = {
        ...response,
        deal_id: cart.value.deal_id || null,
        maker: cart.value.maker,
        date: cart.value.date || false,
        items: cart.value.items,
      } as Cart;
      cartLoading.value = false;

      setCart({ cart: newCart });
    } catch {
      cartLoading.value = false;
    }
  }

  async function validateOrder(payload: {
    items: CartItem[];
    date: { full: string; selectedWindow: string };
    maker: CartMaker;
    deal_id?: string | null;
    orderNotes?: string;
    deliveryNotes?: string;
    what3words?: string;
    deliveryPhones?: string[];
    deliveryEmails?: string[];
    subtotal?: number;
  }) {
    if (!cart.value) return;

    const belowAmount =
      (cart.value.maker?.min_order_amount || 1500) / 100 >
      parseFloat(String((cart.value.items_total || 0) / 100));

    checkoutError.value = false;

    if (belowAmount || cartError.value) {
      order.value = false;
      return;
    }

    cartLoading.value = true;

    const items = payload.items.map((item) => ({
      product_id: item.product.id,
      name: item.product.name,
      quantity: item.quantity,
      appliedModifiers: item.appliedModifiers,
      itemPrice: item.product.price_gross,
    }));

    const org = authStore.organisation;
    const orgAddress = org?.address as Record<string, string> | undefined;
    const orgBilling = org?.billing_address as Record<string, string> | undefined;
    const deliveryAddr = cart.value.deliveryAddress as Record<string, unknown> | undefined;
    const deliveryFormatted = (deliveryAddr as Record<string, Record<string, string>> | undefined)
      ?.formatted;
    const billingAddr = cart.value.billingAddress as Record<string, unknown> | undefined;
    const billingFormatted = (billingAddr as Record<string, Record<string, string>> | undefined)
      ?.formatted;
    const userAddress = (authStore.user as Record<string, unknown> | null)?.address as
      Record<string, string> | undefined;

    const customerData = (customer.value || {}) as Record<string, string>;

    const body: Record<string, unknown> = {
      items,
      same_day: false,
      deal_id: payload.deal_id || null,
      biz: true,
      customer_first_name: customerData.first_name || authStore.user?.first_name,
      customer_last_name: customerData.last_name || authStore.user?.last_name,
      delivery_date_time: `${payload.date.full} ${payload.date.selectedWindow}`,
      maker_id: payload.maker.id,
      discount_id: null,
      customer_phone: customerData.phone || authStore.user?.phone,
      customer_email: customerData.email || authStore.user?.email,
      cost_centre_id: cart.value.cost_centre_id || null,
      // Every invoice-override field must be listed here, not just set on the cart:
      // createOrder builds its body from validateOrder's echoed response, so a field
      // omitted from this payload can never reach POST /orders.
      legal_entity: cart.value.legal_entity || null,
      po_number: cart.value.po_number || null,
      cost_code: cart.value.cost_code || null,
      approver_name: cart.value.approver_name || null,
      custom_invoice_label: cart.value.custom_invoice_label || null,
      note: payload.orderNotes || "",
      delivery_note: payload.deliveryNotes || "",
      what3words: payload.what3words || "",
      delivery_phones: payload.deliveryPhones || [],
      delivery_emails: payload.deliveryEmails || [],
      // Pick a single address source — never mix line_1 from one source
      // with city from another. line_1 is the proxy for "real address". The
      // drop-point coordinates + what3words ride inside the snapshot so the exact
      // location reaches the hub + PedalMe (ops 2026-08-27).
      delivery_address: {
        ...pickAddress(deliveryFormatted, userAddress, orgAddress),
        latitude: (cart.value && cart.value.deliveryLatitude) ?? null,
        longitude: (cart.value && cart.value.deliveryLongitude) ?? null,
        what3words: payload.what3words || null,
        geocoded_address: (cart.value && cart.value.deliveryGeocodedAddress) || null,
        geocoded_zip: (cart.value && cart.value.deliveryGeocodedZip) || null,
      },
      pick_up_address: payload.maker.address || {},
      billing_address: pickAddress(billingFormatted, orgBilling),
      business_name: customerData.business_name || authStore.user?.business_name,
      delivery_service: "",
      organisation_id: org ? org.id : null,
      delivery_fee: org?.subscription?.delivery_gross ?? null,
    };

    const headers: Record<string, string> = authStore.isLoggedIn ? _authHeaders() : {};

    const endpoint = authStore.isLoggedIn
      ? `${config.public.apiBase}/api/customers/orders/validate`
      : `${config.public.apiBase}/api/public/orders/validate`;

    try {
      const response = await $fetch<Order>(endpoint, {
        method: "PUT",
        body,
        headers,
      });
      cartLoading.value = false;
      checkoutError.value = false;
      setOrder(response);
      maybeWriteActiveCheckoutPointer(response);

      // GTM: begin_checkout
      if (typeof window !== "undefined" && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: "begin_checkout",
          currency: "GBP",
          value: (payload.subtotal || 0) / 100,
          items: items
            .filter((item) => item.quantity > 0)
            .map((item) => ({
              item_id: item.product_id,
              item_name: item.name,
              quantity: item.quantity,
              price: (item.itemPrice || 0) / 100,
            })),
        });
      }
    } catch (error: unknown) {
      cartLoading.value = false;
      const msg = extractErrorMessage(error);
      setCheckoutRejection(msg, "validate");
      setOrder(false);
      // If the API rejected the date, surface it as a cart error so the user
      // is prompted to re-select a date/time in the cart pickers.
      if (msg && typeof msg === "string" && /date|time/i.test(msg)) {
        cartError.value = msg;
      }
    }
  }

  async function createOrder(payload: {
    deal_id?: string | null;
    subtotal?: number;
    items?: CartItem[];
    maker?: CartMaker;
  }) {
    // Guard BEFORE touching cartLoading. Setting the flag and then bailing left it
    // stuck true forever — nothing else clears it, since both validateOrder watchers
    // are themselves gated on !cartLoading. Harmless while the flag was unread, but
    // checkout's Next button now disables on it, so the leak would dead-end the
    // customer. Callers detect the no-op via an unchanged `order`.
    if (!order.value) return;

    cartLoading.value = true;
    checkoutError.value = false;

    // Strip fields the backend assigns (DB id, generated references, computed totals,
    // nested associations). Re-creating an order — eg. on billing-method switch —
    // would otherwise replay invoice_number / reference and trip a unique constraint.
    const {
      orderProducts: _op,
      id: _id,
      reference: _ref,
      invoice_number: _inv,
      maker_invoice_number: _mInv,
      commission_invoice_number: _cInv,
      transaction_id: _tx,
      paymentIntent: _pi,
      maker: _maker,
      customer: _customer,
      discount: _discount,
      createdAt: _c,
      updatedAt: _u,
      payment_date: _pd,
      amount_refunded: _ar,
      cancelled_at: _ca,
      ...restOrder
    } = order.value;

    // Always derive items from the cart. order.value comes back from validateOrder
    // with an `items` array, but after a successful createOrder it's replaced by the
    // persisted Order which has `orderProducts` instead.
    const cartItems = (cart.value && cart.value.items) || payload.items || [];
    const items = cartItems.map((item) => ({
      product_id: item.product.id,
      name: item.product.name,
      quantity: item.quantity,
      appliedModifiers: item.appliedModifiers,
      itemPrice: item.product.price_gross,
    }));

    const body = {
      ...restOrder,
      items,
      deal_id: payload.deal_id || null,
      cost_centre_id: cart.value && cart.value.cost_centre_id ? cart.value.cost_centre_id : null,
      billing_method: (cart.value && cart.value.billing_method) || "card",
      // The cart is the live source for these — restOrder is the *previous*
      // validateOrder response, which is stale the moment the customer types
      // into the Delivery step without triggering another validate. Same
      // reasoning as cost_centre_id/billing_method above.
      what3words: (cart.value && cart.value.what3words) || "",
      // Re-merge the drop-point onto the delivery_address snapshot from the live cart
      // (restOrder is the previous validateOrder echo and may not carry it). This is
      // what puts the exact pin on the order for the hub + PedalMe (ops 2026-08-27).
      delivery_address: {
        ...((restOrder as Record<string, unknown>).delivery_address as Record<string, unknown>),
        latitude: (cart.value && cart.value.deliveryLatitude) ?? null,
        longitude: (cart.value && cart.value.deliveryLongitude) ?? null,
        what3words: (cart.value && cart.value.what3words) || null,
        geocoded_address: (cart.value && cart.value.deliveryGeocodedAddress) || null,
        geocoded_zip: (cart.value && cart.value.deliveryGeocodedZip) || null,
      },
      delivery_phones: (cart.value && cart.value.deliveryPhones) || [],
      delivery_emails: (cart.value && cart.value.deliveryEmails) || [],
    };

    const headers: Record<string, string> = authStore.isLoggedIn ? _authHeaders() : {};

    const endpoint = authStore.isLoggedIn
      ? `${config.public.apiBase}/api/customers/orders`
      : `${config.public.apiBase}/api/public/orders`;

    try {
      const response = await $fetch<Order>(endpoint, {
        method: "POST",
        body,
        headers,
      });
      cartLoading.value = false;
      checkoutError.value = false;
      setOrder(response);
      maybeWriteActiveCheckoutPointer(response);
    } catch (error: unknown) {
      cartLoading.value = false;
      setCheckoutRejection(extractErrorMessage(error), "create");
      const status =
        (error as { statusCode?: number }).statusCode ?? (error as { status?: number }).status;
      if (status === 409) guestEmailConflict.value = true;
      handleInvalidToken(error);
      throw error;
    }
  }

  async function createCostCentreOrder(orderId: string | number) {
    try {
      await $fetch(
        `${config.public.apiBase}/api/customers/orders/premium/movePremiumOrderToCreated/${orderId}`,
        { headers: _authHeaders() },
      );
      setShowCart(false);
      navigateTo({ path: "/success", query: { order_id: String(orderId) } });
    } catch (error: unknown) {
      setCheckoutRejection(extractErrorMessage(error), "create");
      throw error;
    }
  }

  async function getOrders(payload: {
    filters?: { filters?: unknown[]; date?: Record<string, unknown> };
    page?: number;
    limit?: number;
    orderBy?: string;
    orderDirection?: string;
  }) {
    if (!authStore.token?.token) {
      const date = payload.filters?.date as { operator?: string } | undefined;
      if (date?.operator === "BEFORE") {
        previousOrders.value = [];
      } else {
        orders.value = [];
      }
      return;
    }

    const filterQuery = generateFilterQueryString(
      (payload.filters?.filters as Array<[string, unknown, boolean?, boolean?]>) || [],
      payload.filters?.date,
    );
    const page = payload.page || 1;
    const limit = payload.limit || 20;
    let query = `?limit=${limit}&page=${page}${filterQuery}`;
    if (payload.orderBy && payload.orderDirection) {
      query += `&orderBy=${payload.orderBy}&orderDirection=${payload.orderDirection}`;
    }

    ordersLoading.value = true;

    try {
      const response = await $fetch<{ orders: Order[] }>(
        `${config.public.apiBase}/api/customers/orders${query}`,
        { headers: _authHeaders() },
      );
      ordersLoading.value = false;
      const date = payload.filters?.date as { operator?: string } | undefined;
      if (date?.operator === "BEFORE") {
        previousOrders.value = response.orders;
      } else {
        orders.value = response.orders;
      }
    } catch (error: unknown) {
      ordersLoading.value = false;
      const date = payload.filters?.date as { operator?: string } | undefined;
      if (date?.operator === "BEFORE") {
        previousOrders.value = [];
      } else {
        orders.value = [];
      }
      handleInvalidToken(error);
    }
  }

  async function getOrganisationOrders(payload: {
    filters?: { filters?: unknown[]; date?: Record<string, unknown> };
    page?: number;
    limit?: number;
    orderBy?: string;
    orderDirection?: string;
  }) {
    if (!authStore.token?.token) {
      const date = payload.filters?.date as { operator?: string } | undefined;
      if (date?.operator === "BEFORE") {
        previousOrders.value = [];
      } else {
        orders.value = [];
      }
      return;
    }

    const filterQuery = generateFilterQueryString(
      (payload.filters?.filters as Array<[string, unknown, boolean?, boolean?]>) || [],
      payload.filters?.date,
    );
    const page = payload.page || 1;
    const limit = payload.limit || 999;
    let query = `?limit=${limit}&page=${page}${filterQuery}`;
    if (payload.orderBy && payload.orderDirection) {
      query += `&orderBy=${payload.orderBy}&orderDirection=${payload.orderDirection}`;
    }

    ordersLoading.value = true;

    try {
      const response = await $fetch<{ orders: Order[] }>(
        `${config.public.apiBase}/api/customers/orders/searchOrdersByOrganisation${query}`,
        { headers: _authHeaders() },
      );
      ordersLoading.value = false;
      const date = payload.filters?.date as { operator?: string } | undefined;
      if (date?.operator === "BEFORE") {
        previousOrders.value = response.orders;
      } else {
        orders.value = response.orders;
      }
    } catch (error: unknown) {
      ordersLoading.value = false;
      const date = payload.filters?.date as { operator?: string } | undefined;
      if (date?.operator === "BEFORE") {
        previousOrders.value = [];
      } else {
        orders.value = [];
      }
      handleInvalidToken(error);
    }
  }

  async function getOrderById(id: string) {
    if (!authStore.token?.token) return null;

    try {
      const response = await $fetch<Order>(`${config.public.apiBase}/api/customers/orders/${id}`, {
        headers: _authHeaders(),
      });
      // Order line items are returned bare — bulk-fetch product images and merge.
      const orderProducts =
        (response as { orderProducts?: Array<{ product?: { id: number } }> }).orderProducts ?? [];
      const productIds = orderProducts
        .map((op) => op.product?.id)
        .filter((pid): pid is number => pid !== undefined && pid !== null);
      if (productIds.length) {
        const byId = await fetchAttachments("product", productIds, ["mainImage"]);
        (response as { orderProducts?: unknown[] }).orderProducts = orderProducts.map((op) =>
          op.product
            ? { ...op, product: { ...op.product, attachments: byId[op.product.id] ?? {} } }
            : op,
        );
      }
      chosenOrder.value = response;
      return response;
    } catch (error: unknown) {
      handleInvalidToken(error);
      return null;
    }
  }

  async function stripePayment(payload: {
    stripe: unknown;
    elements: unknown;
    bookDelivery?: boolean;
  }) {
    cartLoading.value = true;
    checkoutError.value = false;

    await (payload.elements as { submit: () => Promise<unknown> }).submit();
    // Stripe has accepted the card details. Lock navigation away from
    // /checkout (nav-guard reads paymentInitiated) and let the wrapper
    // drive the rest via @confirmed / @failed / @incomplete.
    paymentInitiated.value = true;

    try {
      const invoice = await (
        payload.stripe as {
          confirmPayment: (
            opts: Record<string, unknown>,
          ) => Promise<{ error?: { message: string }; paymentIntent?: Record<string, unknown> }>;
        }
      ).confirmPayment({
        elements: payload.elements,
        redirect: "if_required",
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: `${authStore.user?.first_name} ${authStore.user?.last_name}`,
              email: authStore.user?.email,
            },
          },
        },
      });

      cartLoading.value = false;
      if (invoice.error) {
        setCheckoutRejection(invoice.error.message, "payment");
        // Stripe rejected the card or the user cancelled — payment is
        // no longer in flight. Drop the lock so the cart unblocks; the
        // sessionStorage pointer is preserved so a refresh resumes to
        // the retry view rather than starting over.
        paymentInitiated.value = false;
        throw invoice.error;
      }
      // No hard redirect from inside the action — the StripePaymentWrapper
      // emits @confirmed once the ledger settles via webhook, and the
      // host page (checkout / payment / orders edit) decides what to do.
      return invoice;
    } catch (error: unknown) {
      const message =
        error && typeof error === "object" && "message" in error
          ? String((error as { message: unknown }).message)
          : "Payment failed";
      setCheckoutRejection(message, "payment");
      cartLoading.value = false;
      throw error;
    }
  }

  async function createShareCart(id?: string | null) {
    const cartString = JSON.stringify({ ...cart.value, deal_id: id || null });
    return $fetch(`${config.public.apiBase}/api/public/shareCart`, {
      method: "POST",
      body: { cart: cartString, customer_id: null },
    });
  }

  async function getShareCart(id: string) {
    clearCart();
    return $fetch(`${config.public.apiBase}/api/public/shareCart/${id}`);
  }

  async function getPayment(paymentId: string) {
    cartLoading.value = true;
    try {
      const response = await $fetch<{
        orders: Order[];
        paymentIntent: unknown;
      }>(`${config.public.apiBase}/api/public/orders/payment/${paymentId}`);
      cartLoading.value = false;
      order.value = {
        ...response.orders[0],
        paymentIntent: response.paymentIntent,
      };
    } catch {
      cartLoading.value = false;
      navigateTo("/404");
    }
  }

  async function updatePurchaseOrder(payload: Record<string, unknown>) {
    try {
      await $fetch(`${config.public.apiBase}/api/public/orders/purchaseOrder`, {
        method: "PUT",
        body: payload,
      });
    } catch {
      // Purchase order error
    }
  }

  // Surfaces a "file is being prepared" toast via $notify — the site's mounted
  // notification mechanism (the mitt event bus has no live subscriber; emitting
  // "notify" on it reaches no one). Isolated + throw-proof so a UI-toast failure
  // can never be swallowed as an API error by a surrounding handleInvalidToken.
  function notifyQueued(text: string) {
    try {
      const { $notify } = useNuxtApp() as {
        $notify?: (options: { text: string; variant?: string; timeoutInMs?: number }) => void;
      };
      $notify?.({ text, variant: "info", timeoutInMs: 6000 });
    } catch {
      // a toast failure must never break the download flow
    }
  }

  async function downloadInvoice(payload: { orderId: string; orderRef: string }) {
    if (!authStore.token?.token) return;

    try {
      const data = await $fetch<string | { queued?: boolean; message?: string }>(
        `${config.public.apiBase}/api/customers/orders/downloadInvoice/${payload.orderId}`,
        {
          headers: {
            accept: "application/pdf",
            "x-access-token-customer": authStore.token.token,
            "content-type": "application/octet-stream; charset=utf-8",
          },
        },
      );

      // 202 = the invoice is stale/missing and has been queued for regeneration
      // (downloads never generate inline any more) — tell the customer to retry.
      if (typeof data === "object" && data !== null) {
        if (data.queued) {
          notifyQueued(
            data.message || "Your invoice is being prepared — please try again in a few minutes.",
          );
        }
        // Any other object body is not a base64 PDF — never feed it to atob.
        return;
      }

      const binaryString = window.atob(data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const url = window.URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `&Dine Invoice - ${payload.orderRef}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: unknown) {
      handleInvalidToken(error);
    }
  }

  // Lists a customer's downloadable files (invoice, allergen matrix) for an order —
  // backs the file-history modal. Returns [] on error.
  async function listOrderFiles(orderId: string) {
    if (!authStore.token?.token) return [];
    try {
      const res = await $fetch<{
        files: Array<{
          attachmentId: string;
          collection: string;
          fileName: string;
          generatedAt: string;
        }>;
      }>(`${config.public.apiBase}/api/customers/fileGeneration/files/${orderId}`, {
        headers: { "x-access-token-customer": authStore.token.token },
      });
      return res.files ?? [];
    } catch (error: unknown) {
      handleInvalidToken(error);
      return [];
    }
  }

  // Downloads a specific generated file by attachment id (raw stream → blob).
  async function downloadFileAttachment(attachmentId: string, fileName: string) {
    if (!authStore.token?.token) return;
    try {
      const blob = await $fetch<Blob>(
        `${config.public.apiBase}/api/customers/fileGeneration/attachment/${attachmentId}`,
        {
          headers: { "x-access-token-customer": authStore.token.token },
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || "file");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      handleInvalidToken(error);
    }
  }

  async function downloadAllergenMatrix(payload: { orderId: string; orderRef: string }) {
    if (!authStore.token?.token) return;

    try {
      const blob = await $fetch<Blob>(
        `${config.public.apiBase}/api/customers/orders/downloadAllergenMatrix/${payload.orderId}`,
        {
          headers: { "x-access-token-customer": authStore.token.token },
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `&Dine Allergen Matrix - ${payload.orderRef}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      handleInvalidToken(error);
    }
  }

  function removeCancelledOrderFromStore(orderId: string | number, orderType?: string) {
    if (!orderId) return;

    const oId = Number(orderId);

    // Daily orders live in the premium store's organisationOrders array.
    // Single/Event orders live in this store's orders / previousOrders arrays.
    if (orderType === "Daily") {
      const premiumStore = usePremiumStore();
      premiumStore.removeOrganisationOrder(oId);
      return;
    }

    if (orders.value && Array.isArray(orders.value)) {
      orders.value = orders.value.filter((o) => o.id !== oId);
    }
    if (previousOrders.value && Array.isArray(previousOrders.value)) {
      previousOrders.value = previousOrders.value.filter((o) => o.id !== oId);
    }
  }

  async function modifyOrder(payload: {
    orderId: string | number;
    products?: {
      product_id: number;
      quantity: number;
      order_product_id?: number | null;
      modifiers?: { modifier_id: number; quantity: number }[];
    }[];
    deliveryDateTime?: string | null;
  }) {
    cartLoading.value = true;
    checkoutError.value = false;
    rebookFailedMessage.value = false;
    try {
      const body: Record<string, unknown> = {};
      if (payload.products) body.products = payload.products;
      if (payload.deliveryDateTime) body.delivery_date_time = payload.deliveryDateTime;

      const response = await $fetch<{
        requires_payment: boolean;
        requires_refund: boolean;
        payment_intent?: { client_secret: string; amount?: number };
        order?: Order;
        status?: string;
        refund_status?: string;
        refund_batch_id?: string | null;
        initial_state?: string | null;
      }>(`${config.public.apiBase}/api/customers/orders/modify/${payload.orderId}`, {
        method: "POST",
        body,
        headers: _authHeaders(),
      });
      cartLoading.value = false;
      return response;
    } catch (error: unknown) {
      cartLoading.value = false;
      // HTTP 502 with a structured failure payload is a known whole-batch refund
      // failure. Return the body so the caller can render the terminal UI
      // instead of treating it as an unknown error.
      const fetchError = error as { statusCode?: number; data?: Record<string, unknown> };
      if (
        fetchError?.statusCode === 502 &&
        fetchError?.data?.status === "failure" &&
        fetchError?.data?.requires_refund
      ) {
        return fetchError.data as never;
      }
      // A date/time change re-books the courier in the background; if PedalMe can't re-book,
      // the backend reverts the date change and returns 409 { rebookFailed: true }. Surface it
      // via the dedicated modal (not the generic banner) so the customer knows the change wasn't
      // saved and the original date stands.
      const rebookData = fetchError?.data as { rebookFailed?: boolean; message?: string } | undefined;
      if (rebookData?.rebookFailed) {
        rebookFailedMessage.value =
          rebookData.message ||
          "We couldn't re-book the courier for that delivery time, so the change wasn't saved. Please pick a different time or contact us.";
        handleInvalidToken(error);
        throw error;
      }
      setCheckoutRejection(extractErrorMessage(error), "modify");
      handleInvalidToken(error);
      throw error;
    }
  }

  /**
   * Public retry endpoint — creates a fresh PAY ledger + Stripe PaymentIntent
   * for an order whose previous payment failed, and invalidates the old
   * ledger so it stops blocking the cart. The Stripe payment_intent_id of
   * the failed attempt is the capability token (no JWT required), so this
   * also works for guest checkouts.
   *
   * Returns 409 from the backend if a refund batch is in flight against
   * the same order — error propagates so the caller (StripePaymentWrapper)
   * can surface it.
   */
  async function retryPayment(payload: { paymentIntentId: string }): Promise<{
    payment_intent: { id: string; client_secret: string; status?: string; [key: string]: unknown };
    ledger: Record<string, unknown>;
  }> {
    cartLoading.value = true;
    try {
      const response = await $fetch<{
        payment_intent: {
          id: string;
          client_secret: string;
          status?: string;
          [key: string]: unknown;
        };
        ledger: Record<string, unknown>;
      }>(`${config.public.apiBase}/api/public/orders/payments/retry/${payload.paymentIntentId}`, {
        method: "POST",
        body: {},
      });
      cartLoading.value = false;
      // Reset the in-session Stripe status — the new PI starts fresh.
      stripePaymentIntentStatus.value = null;
      return response;
    } catch (error: unknown) {
      cartLoading.value = false;
      cartError.value = extractErrorMessage(error);
      // Surface a descriptive error at the call boundary; original is
      // preserved on `cause` for debuggers / future Sentry hookup.
      throw new Error("Failed to retry payment", { cause: error });
    }
  }

  async function cancelOrderById(orderId: string | number, reason?: string) {
    try {
      const response = await $fetch<{
        requires_refund?: boolean;
        status?: string;
        refund_status?: string;
        refund_batch_id?: string | null;
        initial_state?: string | null;
      }>(`${config.public.apiBase}/api/customers/orders/${orderId}/cancel`, {
        method: "DELETE",
        headers: _authHeaders(),
        ...(reason ? { body: { cancellation_reason: reason } } : {}),
      });
      return response;
    } catch (error: unknown) {
      // HTTP 502 with a structured failure payload is a known whole-batch refund
      // failure. Return the body so the caller can render the terminal UI
      // instead of treating it as an unknown error.
      const fetchError = error as { statusCode?: number; data?: Record<string, unknown> };
      if (
        fetchError?.statusCode === 502 &&
        fetchError?.data?.status === "failure" &&
        fetchError?.data?.requires_refund
      ) {
        return fetchError.data;
      }
      handleInvalidToken(error);
      throw error;
    }
  }

  return {
    // State
    cartType,
    cart,
    cartError,
    checkoutError,
    checkoutErrorSource,
    rebookFailedMessage,
    clearRebookFailed: () => {
      rebookFailedMessage.value = false;
    },
    guestEmailConflict,
    cartLoading,
    showCart,
    cartLocked,
    order,
    orders,
    previousOrders,
    ordersLoading,
    chosenOrder,
    orderNotes,
    deliveryNotes,
    what3words,
    deliveryPhones,
    deliveryEmails,
    customer,
    dealId,
    packageCart,
    stripePaymentIntentStatus,
    setStripePaymentIntentStatus,
    paymentInitiated,
    setPaymentInitiated,

    // Getters
    cartItemCount,

    // Edit mode state
    cartMode,
    hasUnsavedChanges,
    initialCartItems,

    // Mutations
    setShowCart,
    setCartLocked,
    setPackageCart,
    setCartType,
    setCartMode,
    setCart,
    setCartError,
    setCartLoading,
    setOrderNotes,
    setDeliveryNotes,
    setWhat3Words,
    setDeliveryCoords,
    setDeliveryGeocoded,
    setDeliveryPhones,
    setDeliveryEmails,
    setCustomer,
    setDeliveryDate,
    setSelectedWindow,
    setDeliveryWindows,
    setDeliveryAddress,
    setBillingAddress,
    setOrder,
    setChosenOrder,
    setOrders,
    setPreviousOrders,
    setCheckoutError,
    setDealId,
    setHasUnsavedChanges,
    setInitialCartItems,
    resetCart,
    clearCartLocal,
    setCostCentreId,
    setInvoiceOverrides,
    setBillingMethod,

    // Actions
    enterStore,
    leaveStore,
    clearCart,
    clearCartForUnavailableMaker,
    clearOrder,
    addToCart,
    removeFromCart,
    recreateCart,
    recalculateCart,
    validateOrder,
    createOrder,
    createCostCentreOrder,
    getOrders,
    getOrganisationOrders,
    getOrderById,
    stripePayment,
    createShareCart,
    getShareCart,
    getPayment,
    updatePurchaseOrder,
    downloadInvoice,
    downloadAllergenMatrix,
    listOrderFiles,
    downloadFileAttachment,
    cancelOrderById,
    modifyOrder,
    retryPayment,
    resumeActiveCheckout,
    clearActiveCheckout,
    removeCancelledOrderFromStore,
  };
});
